import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { findInsuranceBySlug, insuranceTypes, partnerInsurers, COMPANY } from "../data/insurances";

export default function ProductPage() {
  const { slug } = useParams();
  const insurance = findInsuranceBySlug(slug);

  if (!insurance) return <Navigate to="/" replace />;

  const related = insuranceTypes.filter((i) => i.slug !== slug).slice(0, 3);

  // map provider names to logo info from partnerInsurers (fallback to colored badge)
  const providerData = insurance.providers.map((name) => {
    const match = partnerInsurers.find(
      (p) => p.name.toLowerCase() === name.toLowerCase() ||
             p.name.toLowerCase().includes(name.toLowerCase()) ||
             name.toLowerCase().includes(p.name.toLowerCase())
    );
    return match || { name, color: "#0F2A5C", logoUrl: null };
  });

  return (
    <div data-testid={`product-page-${slug}`}>
      {/* Hero */}
      <section className="bg-gradient-to-br from-navy-50 via-white to-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 brick-grid-bg opacity-60" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-lego font-semibold mb-8"
          >
            <i className="fa-solid fa-arrow-left text-xs" /> Todos os seguros
          </Link>

          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-lego/10 text-lego px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-6">
                <i className={`fa-solid ${insurance.icon}`} />
                {insurance.short}
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-navy leading-[1.05] tracking-tight">
                {insurance.name}
              </h1>
              <p className="mt-6 text-lg text-navy/70 leading-relaxed max-w-2xl">
                {insurance.description}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  asChild
                  size="lg"
                  className="bg-brand hover:bg-brand-600 active:bg-brand-700 text-white rounded-full font-bold h-14 px-8 shadow-soft-lg hover:-translate-y-1 transition-all"
                  data-testid="product-cta-cotar"
                >
                  <Link to={`/cotacao?tipo=${insurance.slug}`}>
                    <i className="fa-solid fa-shield-heart mr-2" />
                    Cotar {insurance.short}
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="rounded-full font-semibold h-14 px-8 border-2 border-navy text-navy hover:bg-navy hover:text-white"
                >
                  <a
                    href={`https://wa.me/55${COMPANY.whatsapp}?text=${encodeURIComponent(
                      `Olá! Gostaria de falar sobre ${insurance.name}.`
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i className="fa-brands fa-whatsapp mr-2 text-xl" /> Falar com especialista
                  </a>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-navy rounded-3xl p-8 text-white shadow-soft-lg relative overflow-hidden">
                <div className="absolute inset-0 brick-pattern opacity-30" />
                <div className="relative">
                  <p className="text-xs uppercase tracking-widest text-white/50 font-bold mb-3">
                    Seguradoras parceiras
                  </p>
                  <div className="grid grid-cols-2 gap-2.5 mb-6" data-testid="product-providers">
                    {providerData.map((p) => (
                      <div
                        key={p.name}
                        className="bg-white rounded-xl px-3 py-2.5 flex items-center gap-2.5 border border-white/20"
                      >
                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shrink-0 overflow-hidden">
                          {p.logoUrl ? (
                            <>
                              <img
                                src={p.logoUrl}
                                alt={`Logo ${p.name}`}
                                className="max-h-8 max-w-8 object-contain"
                                loading="lazy"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  const fb = e.currentTarget.nextElementSibling;
                                  if (fb) fb.style.display = "flex";
                                }}
                              />
                              <div
                                className="hidden w-8 h-8 rounded-md items-center justify-center text-white font-heading font-bold text-sm"
                                style={{ backgroundColor: p.color }}
                              >
                                {p.name.charAt(0)}
                              </div>
                            </>
                          ) : (
                            <div
                              className="w-8 h-8 rounded-md flex items-center justify-center text-white font-heading font-bold text-sm"
                              style={{ backgroundColor: p.color }}
                            >
                              {p.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <span className="text-navy font-heading font-bold text-xs leading-tight">
                          {p.name}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs uppercase tracking-widest text-white/50 font-bold mb-3 mt-8">
                    O que você ganha
                  </p>
                  <ul className="space-y-3">
                    {insurance.benefits.slice(0, 5).map((b) => (
                      <li key={b} className="flex gap-3 text-sm leading-relaxed">
                        <i className="fa-solid fa-check text-lego mt-1" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full benefits */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-navy mb-10">
            Coberturas e benefícios
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {insurance.benefits.map((b, i) => (
              <div
                key={i}
                className="bg-surface rounded-2xl p-6 flex gap-4 border border-navy-100"
              >
                <div className="w-10 h-10 rounded-xl bg-lego/10 flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-check text-lego" />
                </div>
                <p className="text-navy/80 leading-relaxed font-medium">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16 bg-surface">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-navy mb-8">
            Você também pode se interessar
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/seguros/${r.slug}`}
                className="bg-white rounded-3xl p-6 border border-navy-100 hover:border-lego hover:shadow-soft transition-all"
              >
                <div className="w-12 h-12 bg-navy-50 rounded-xl flex items-center justify-center mb-4">
                  <i className={`fa-solid ${r.icon} text-navy`} />
                </div>
                <p className="font-heading font-bold text-navy mb-1">{r.name}</p>
                <p className="text-sm text-navy/60">{r.tagline}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
